import React from 'react';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  Ban,
  RefreshCw,
  Layers,
  TrendingUp,
  ClipboardList,
  Sparkles,
  CheckCircle2,
  XCircle,
  Lightbulb,
  Target,
  FileText,
  Zap,
  AlertTriangle,
  Rocket,
  Briefcase,
  Users,
  Building2,
  Store
} from 'lucide-react';
import Logo from '../Logo';
import { SEO } from '../SEO';
import { Button } from '../ui/Button';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'wouter';
import { SupportFooter } from '../SupportFooter';

const painPoints = [
  {
    icon: RefreshCw,
    title: "Every checklist recommends different tools",
    description: "One says QuickBooks, another says Wave, another says Xero. You're more confused than when you started."
  },
  {
    icon: Layers,
    title: "Most lists are bloated with unnecessary software",
    description: "Do you really need 15 tools to start a business? Probably not."
  },
  {
    icon: AlertTriangle,
    title: "Tools overlap and do the same things",
    description: "You end up paying for three apps that all handle invoicing slightly differently."
  },
  {
    icon: TrendingUp,
    title: "What you need today isn't what you'll need later",
    description: "Some tools are overkill now. Others you'll outgrow in months. Which is which?"
  },
  {
    icon: CreditCard,
    title: "Choosing wrong means wasted money and switching pain",
    description: "Lock into the wrong platform and you're stuck with costly migrations down the road."
  }
];

const blueprintComponents = [
  {
    icon: Target,
    name: "Core Stack",
    description: "What to use right now based on your actual needs"
  },
  {
    icon: TrendingUp,
    name: "Growth Stack",
    description: "What to add later as your business evolves"
  },
  {
    icon: XCircle,
    name: "Avoid List",
    description: "Tools to skip so you don't overpay or overcomplicate"
  },
  {
    icon: FileText,
    name: "Plain-English Explanations",
    description: "Why each recommendation fits your situation"
  },
  {
    icon: Zap,
    name: "Clear Upgrade Path",
    description: "Know exactly when and how to scale your tools"
  }
];

const steps = [
  {
    icon: ClipboardList,
    title: "Answer a few questions about your business",
    description: "What you do, who you serve, and where you're headed"
  },
  {
    icon: Sparkles,
    title: "We analyze what actually fits your situation",
    description: "No generic advice, just what works for you"
  },
  {
    icon: CheckCircle2,
    title: "Get a clear software plan you can act on immediately",
    description: "Know exactly what to use and what to skip"
  }
];

const whoThisIsFor = [
  {
    icon: Rocket,
    text: "New business owners figuring out their first tools"
  },
  {
    icon: Building2,
    text: "Side hustlers and solopreneurs keeping costs low"
  },
  {
    icon: Briefcase,
    text: "First-time founders who don't want to waste money"
  },
  {
    icon: Users,
    text: "Anyone overwhelmed by conflicting software advice"
  },
  {
    icon: Store,
    text: "Small businesses that want to start clean and scale smart"
  }
];

const differentiators = [
  {
    icon: XCircle,
    text: "No generic checklists that don't fit your situation"
  },
  {
    icon: Shield,
    text: "No paid placements or sponsored recommendations"
  },
  {
    icon: Ban,
    text: "No enterprise software you'll never need"
  },
  {
    icon: AlertTriangle,
    text: "Tells you what NOT to buy — not just what to buy"
  },
  {
    icon: CreditCard,
    text: "One-time payment — no subscriptions, no upsells"
  }
];

const faqItems = [
  {
    question: "What is the Business Software Blueprint?",
    answer: "A personalized software plan that includes your recommended tools, what to avoid, and a clear upgrade path as your business grows."
  },
  {
    question: "How long does it take?",
    answer: "About 3 minutes to complete the questionnaire, then instant access to your full Blueprint."
  },
  {
    question: "What if it doesn't help me?",
    answer: "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you — no questions asked."
  }
];

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Choose Tools Needed to Start a Business",
  "description": "Get a personalized software blueprint instead of generic tool checklists",
  "totalTime": "PT3M",
  "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "29" },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Answer Questions About Your Business",
      "text": "Tell us what kind of business you're starting, your goals, expected team size, and your budget for software.",
      "url": "https://btoolme.com/questionnaire",
      "itemListElement": [
        { "@type": "HowToTip", "text": "New businesses often overbuy software. Answering honestly helps us recommend only what you actually need to launch." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "We Analyze What Fits Your Situation",
      "text": "Your answers are matched against 100+ tools to find what's right for a business at your exact stage — filtering out enterprise tools and overbuilt software.",
      "itemListElement": [
        { "@type": "HowToTip", "text": "We include an Avoid List so you know which popular tools aren't right for a new business." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Get a Clear Software Plan",
      "text": "Receive your Business Software Blueprint with a Core Stack for now, a Growth Stack for later, and an Avoid List to prevent costly mistakes.",
      "url": "https://btoolme.com/unlock",
      "itemListElement": [
        { "@type": "HowToTip", "text": "The upgrade path tells you exactly when to add new tools — so you don't overbuy on day one." }
      ]
    }
  ]
};

export function ToolsNeededLandingPage() {
  const [_, navigate] = useLocation();

  const seoTitle = "Tools Needed to Start a Business (Without Overbuying Software) | btoolme";
  const seoDescription = "Skip the bloated checklists. btoolme builds a personalized software blueprint so you know exactly what tools to use, what to skip, and when to upgrade.";

  const guaranteeText = "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you — no questions asked.";

  return (
    <div className="min-h-screen flex flex-col" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/tools-needed-to-start-a-business"
        faqItems={faqItems}
        keywords="tools needed to start a business, startup tools, business startup software, tools for new business, software to start a business, essential startup tools, business launch software, new business checklist software, entrepreneur tools, starting a business software, business tools for beginners, startup essentials"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-gray-50/50 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-slate-200/30 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-gray-100/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100/40 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <Logo className="mb-8 mx-auto" />

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              Tools Needed to Start a Business
              <span className="block text-3xl sm:text-4xl lg:text-5xl text-gray-600 mt-2">
                (Without Overbuying Software)
              </span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-10 font-medium max-w-3xl mx-auto leading-relaxed">
              Many new businesses struggle not because they lack tools — but because they choose the wrong ones. btoolme builds a clear software blueprint so you know what to use, what to skip, and when.
            </p>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg sparkle-button w-full sm:w-auto"
                size="lg"
                aria-label="Build your personalized software blueprint"
              >
                Build My Software Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-sm text-gray-600 max-w-md text-center leading-relaxed">
                {guaranteeText}
              </p>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-600" />
                <span>One-time payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-slate-600" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Checklist Anxiety */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why "Tool Checklists" Make This Harder Than It Needs to Be
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {painPoints.slice(0, 4).map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-5 mt-5 max-w-md mx-auto sm:max-w-lg">
            {painPoints.slice(4).map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reframe Section - Key Insight */}
      <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-200 rounded-2xl mb-6">
            <Lightbulb className="w-8 h-8 text-slate-700" />
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            You Don't Need More Tools — You Need Fewer, Better Decisions
          </h2>

          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            There is no universal "starter tool list" that works for everyone. The right tools depend entirely on your business type, your goals, your budget, and where you are right now.
          </p>

          <p className="text-gray-600 leading-relaxed mb-8">
            The real mistake isn't picking the wrong tool — it's buying tools before you've made the decisions that matter.
          </p>

          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl mx-auto">
            <p className="text-slate-800 font-medium">
              This page will not give you a giant checklist — because that's not what actually helps.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section - The Blueprint */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              A Smarter Way to Choose Your Business Tools
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Instead of guessing or copying someone else's stack, btoolme creates a personalized Business Software Blueprint for your situation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {blueprintComponents.slice(0, 3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm">{component.description}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5 max-w-2xl mx-auto lg:max-w-none lg:px-24">
            {blueprintComponents.slice(3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 p-6 hover:border-gray-300 transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm">{component.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-slate-300 to-transparent" />
                  )}

                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/20">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-slate-900 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-gray-900 max-w-[220px]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-500 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Who This Is For
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whoThisIsFor.slice(0, 3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-100">
                    <Icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <span className="text-gray-700 text-sm pt-2">{item.text}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-2xl mx-auto">
            {whoThisIsFor.slice(3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-100">
                    <Icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <span className="text-gray-700 text-sm pt-2">{item.text}</span>
                </div>
              );
            })}
          </div>

          <p className="mt-8 text-center text-sm text-gray-500">
            Works for online businesses, service businesses, trades, freelancers, and more.
          </p>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Why btoolme Is Different From Tool Lists
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.slice(0, 3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-2xl mx-auto">
            {differentiators.slice(3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Start Your Business With Confidence — Not Guesswork
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get your personalized Business Software Blueprint and move forward knowing you chose right.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={() => navigate('/questionnaire')}
              className="group px-10 py-5 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Software Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-sm text-gray-400 max-w-md text-center mt-2">
              {guaranteeText}
            </p>
          </div>

          <p className="mt-10 text-sm text-gray-500">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-400 hover:text-white underline">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-10 bg-slate-900 border-t border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4 text-center">Related Guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href="/tools" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Browse All 100+ Tools</p>
              <p className="text-xs text-slate-400">Full curated business software directory</p>
            </a>
            <a href="/what-software-should-a-small-business-use" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Small Business Software Guide</p>
              <p className="text-xs text-slate-400">How to choose the right software for your situation</p>
            </a>
            <a href="/for-trades" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Software for Trades</p>
              <p className="text-xs text-slate-400">Contractors, plumbers, electricians & more</p>
            </a>
          </div>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-slate-900" />
    </div>
  );
}
